import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { IonRow, IonCol, IonLabel, IonCard, IonCardContent } from "@ionic/react";
import BasicTextarea from "../../TextArea/BasicTextarea";
import SubSectionCard from "./SubSectionCard";
import EntrySubSectionCard from "./EntrySubSectionCard";
import { buildEntryIndexById } from "./entrySubCardUtils";
import EntryStatusChip from "./EntryStatusChip";
import { BeddingRequest, Event, EventBeddingType, EventEntry, EventEntryFee, EventFee, EventStallType, StablingRequest } from "../../../models";
import { PersonContext } from "../../../context/PersonContext";
import { createStablingRequest, deleteStablingRequest, updateStablingRequest } from "../../../utilities/stablingRequest/StablingRequest";
import { getFormattedStablingOptionDataForEventStalls, StablingAndExtrasItem, StablingAndExtrasOptionsData } from "../../../utilities/eventStallType/FormatStablingOptionsUtil";
import { getFormattedStablingOptionDataForEventBedding } from "../../../utilities/eventBeddingType/FormatEventBeddingOptionsUtil";
import { SubSectionCardsSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import {
    CreateBeddingRequestInput,
    CreateEventEntryFeeInput,
    CreateStablingRequestInput,
    UpdateBeddingRequestInput,
    UpdateEventEntryFeeInput,
    UpdateStablingRequestInput,
} from "../../../API";
import { createBeddingRequest, deleteBeddingRequest, updateBeddingRequest } from "../../../utilities/beddingRequest/BeddingRequest";
import { getEventBeddingTypeById } from "../../../utilities/eventBeddingType/EventBeddingType";
import StablingExtrasOrderEditor from "./StablingExtrasOrderEditor";
import {
    barnAccountFeeDescription,
    FormattedEntryData,
    FormattedRow,
    getFormattedEntryData,
    getPreviousBeddingRequests,
    getPreviousStablingRequests,
    groupCompletedEntriesByBarn,
    isBarnAccountEntry,
    isBarnAccountStablingOrBeddingRequest,
} from "../../../utilities/stablingRequest/StablingAndExtrasUtil";
import { getFormattedFeeOptionDataForEventFees } from "../../../utilities/eventFee/FormatEventFeeOptionsUtil";
import { createEventEntryFee, deleteEventEntryFee, updateEventEntryFee } from "../../../utilities/eventEntryFee/EventEntryFee";

const basicStablingOptions = [
    { label: "Stalls", options: [] },
    { label: "Bedding", options: [] },
    { label: "Extras", options: [] },
];

/**
 * Imperative API exposed via `ref`. The parent card owns the Save button and decides
 * when to collapse; this handle lets it ask the section to persist its pending edits first.
 */
export interface StablingAndExtrasSectionHandle {
    /** Persist any pending stabling/bedding edits. Resolves once the network work completes. */
    save: () => Promise<void>;
}

interface StablingAndExtrasSectionProps {
    event: Event;
    completedEventEntries?: EventEntry[];
    completedStablingData?: FormattedEntryData[];
    completedStablingOptions?: StablingAndExtrasOptionsData[];
    /**
     * Called when the user mutates stabling/bedding rows (add, remove, quantity change, save).
     * Receives the full updated dataset so the parent can keep its cache in sync — matches the
     * shape used by `WaiversSection.onItemsChange` for consistency.
     */
    onItemsChange?: (updatedData: FormattedEntryData[]) => void;
    onDirtyChange?: (isDirty: boolean) => void;
    /** Drives the parent section card status chip (`Loading`) instead of an in-content spinner. */
    onLoadingChange?: (loading: boolean) => void;
    /** Bumps when navigating from Entry Summary so entry subcards remount with the correct expand state. */
    stablingAccordionFocusKey?: number;
    /** When set, only this `EventEntry.id` stabling sub-card is expanded; others start collapsed. */
    expandStablingOnlyForEventEntryId?: string;
}

const StablingAndExtrasSection = forwardRef<StablingAndExtrasSectionHandle, StablingAndExtrasSectionProps>(
    ({ event, completedEventEntries, completedStablingData, onItemsChange, onDirtyChange, onLoadingChange, stablingAccordionFocusKey = 0, expandStablingOnlyForEventEntryId }, ref) => {
        const user = useContext(PersonContext);

        const [isSectionLoading, setIsSectionLoading] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [isRemoving, setIsRemoving] = useState(false);
        const [stablingNote, setStablingNote] = useState<string>("");

        const markDirty = () => {
            onDirtyChange?.(true);
        };

        const clearDirty = () => {
            onDirtyChange?.(false);
        };

        const [formattedEntryData, setFormattedEntryData] = useState<FormattedEntryData[]>(completedStablingData || []);

        const [previousStablingRequests, setPreviousStablingRequests] = useState<StablingRequest[] | null | undefined>();
        const [previousBeddingRequests, setPreviousBeddingRequests] = useState<BeddingRequest[] | null | undefined>();
        const [previousEventEntryFees, setPreviousEventEntryFees] = useState<EventEntryFee[] | null | undefined>();

        const [dropdownMenuOptions, setDropDownMenuOptions] = useState<StablingAndExtrasOptionsData[]>(basicStablingOptions);

        const getEventStablingData = async (event: Event) => {
            const stallStablingOption: StablingAndExtrasOptionsData = await getFormattedStablingOptionDataForEventStalls(event);
            const beddingStablingOption: StablingAndExtrasOptionsData = await getFormattedStablingOptionDataForEventBedding(event);
            const extrasOption: StablingAndExtrasOptionsData = await getFormattedFeeOptionDataForEventFees(event);
            const updatedStablingOptions = [stallStablingOption, beddingStablingOption, extrasOption];
            setDropDownMenuOptions(updatedStablingOptions);
        };

        const getPreviousStablingNote = () => {
            let note = "";
            if (formattedEntryData && formattedEntryData.length > 0) {
                for (let i = 0; i < formattedEntryData.length; i++) {
                    const entry = formattedEntryData[i];
                    if (entry.rows && entry.rows.length > 0) {
                        for (let j = 0; j < entry.rows.length; j++) {
                            const row = entry.rows[j];
                            if (row.item.previousStablingRequest && row.item.previousStablingRequest.requestNote) {
                                note = row.item.previousStablingRequest.requestNote;
                                break;
                            }
                        }
                    }
                }
            }
            return note;
        }

        /**
         * Fetch the supporting data this section needs on open:
         *   - Stabling/bedding option dropdowns (required to add new items).
         *   - Previous stabling/bedding requests (required to delete already-saved items).
         *
         * Entry rows themselves come pre-cached from the parent via `completedStablingData`,
         * so we skip the redundant `getFormattedEntryData` fetch when that cache is populated.
         * When there is no cached data, the parent card status chip shows `Loading` via
         * `onLoadingChange` instead of a full-section spinner so card height stays stable.
         */
        const getDataOnLoad = async () => {
            const hasCachedEntryRows = formattedEntryData && formattedEntryData.length > 0;
            if (!hasCachedEntryRows) {
                setIsSectionLoading(true);
                onLoadingChange?.(true);
            }

            let prevStablingNote = getPreviousStablingNote();
            setStablingNote(prevStablingNote);

            if (event) await getEventStablingData(event);

            if (!hasCachedEntryRows && completedEventEntries) {
                const results = await getFormattedEntryData(event, user, completedEventEntries);
                setFormattedEntryData(results);
            }

            const prevStablingRequests = await getPreviousStablingRequests(user, event);
            const prevBeddingRequests = await getPreviousBeddingRequests(user, event);

            setPreviousStablingRequests(prevStablingRequests);
            setPreviousBeddingRequests(prevBeddingRequests);

            const barnGroups = groupCompletedEntriesByBarn(completedEventEntries ?? []);
            const cachedBarnAccountCount = formattedEntryData.filter((d) => isBarnAccountEntry(d)).length;
            const needsBarnAccountRefresh =
                hasCachedEntryRows &&
                barnGroups.length > 0 &&
                (cachedBarnAccountCount !== barnGroups.length || formattedEntryData.some((d) => d.id === "barn-account"));
            if (needsBarnAccountRefresh && completedEventEntries) {
                const refreshed = await getFormattedEntryData(event, user, completedEventEntries);
                setFormattedEntryData(refreshed);
                onItemsChange?.(refreshed);
            }

            if (!hasCachedEntryRows) {
                setIsSectionLoading(false);
                onLoadingChange?.(false);
            }
        };

        useEffect(() => {
            return () => {
                onLoadingChange?.(false);
            };
        }, [onLoadingChange]);

        useEffect(() => {
            if (completedEventEntries) {
                let needsStablingOptions = true;
                if (dropdownMenuOptions && dropdownMenuOptions.length > 0) {
                    for (let i = 0; i < dropdownMenuOptions.length; i++) {
                        const current = dropdownMenuOptions[i];
                        if (current.options.length > 0) {
                            needsStablingOptions = false;
                            break;
                        }
                    }
                }
                if (needsStablingOptions) getDataOnLoad();
            }
        }, [completedEventEntries]);

        useEffect(() => {
            if (!completedStablingData) return;
            const propEntryIds = completedStablingData
                .filter((d) => !isBarnAccountEntry(d))
                .map((d) => d.eventEntry?.id)
                .filter(Boolean)
                .sort()
                .join(",");
            const localEntryIds = formattedEntryData
                .filter((d) => !isBarnAccountEntry(d))
                .map((d) => d.eventEntry?.id)
                .filter(Boolean)
                .sort()
                .join(",");
            if (propEntryIds !== localEntryIds) {
                setFormattedEntryData(completedStablingData);
            }
        }, [completedStablingData]);

        /**
         * Imperative save called by the parent card's Save button. Replaces the previous
         * `continueClicks` counter pattern so the parent decides when to collapse and the
         * section just handles persistence.
         */
        useImperativeHandle(ref, () => ({
            save: async () => {
                await handleSaveData();
            },
        }));

        const handleOnChange = (index: number, item: StablingAndExtrasItem) => {
            let updatedFormattedEntryData: FormattedEntryData = formattedEntryData[index];
            let updatedItemsList: FormattedRow[] = updatedFormattedEntryData.rows;

            const foundIndex = updatedItemsList.findIndex((r: FormattedRow) => r.item.value === item.value);
            if (foundIndex === -1) {
                const newFormattedRow: FormattedRow = {
                    item: item,
                    quantity: 1, //default value when a new item is selected from the search bar is 1
                };
                // Add the new item to the top of the list of rows
                updatedItemsList = [newFormattedRow].concat(updatedItemsList);
            } else {
                // If user clicks on an item from the search bar that was already present in the list, do nothing
            }

            updatedFormattedEntryData = {
                ...updatedFormattedEntryData,
                rows: updatedItemsList,
            };

            const formattedEntryDataList: FormattedEntryData[] = [...formattedEntryData.slice(0, index), updatedFormattedEntryData, ...formattedEntryData.slice(index + 1)];
            setFormattedEntryData(formattedEntryDataList);
            onItemsChange?.(formattedEntryDataList);
            markDirty();
        };

        const eventEntries = useMemo(() => completedEventEntries ?? [], [completedEventEntries]);

        /** Barn accounts require at least one entry (future: optional barn picker without entries). */
        useEffect(() => {
            if (eventEntries.length > 0) return;
            setFormattedEntryData((prev) => {
                const withoutBarnAccounts = prev.filter((d) => !isBarnAccountEntry(d));
                if (withoutBarnAccounts.length === prev.length) return prev;
                onItemsChange?.(withoutBarnAccounts);
                return withoutBarnAccounts;
            });
        }, [eventEntries.length, onItemsChange]);

        const handleRemoveItem = async (entryIndex: number, rowIndex: number) => {
            setIsRemoving(true);
            let updatedFormattedEntryData: FormattedEntryData = formattedEntryData[entryIndex];
            const forBarnAccount = isBarnAccountEntry(updatedFormattedEntryData);
            let updatedItemsList: FormattedRow[] = updatedFormattedEntryData.rows;
            let updatedItem: FormattedRow = updatedItemsList[rowIndex];

            if (updatedItem.item.previousStablingRequest) {
                // Find all stabling requests that need to be deleted
                // Note - we want to delete StablingRequests - we do not want to delete the EventStallTypes
                // This method of finding all matching stabling requests will remove all rows if there are duplicates of an EventStallType for an entry
                const stablingRequestsToDelete: StablingRequest[] = (previousStablingRequests ?? []).filter((sr: StablingRequest) => {
                    const stablingRequestMatchesEventStallType = sr.stallType?.id === updatedItem.item.eventStallType?.id;
                    if (forBarnAccount) {
                        const matchesBarn = !updatedFormattedEntryData.barnId || sr.barnId === updatedFormattedEntryData.barnId;
                        return stablingRequestMatchesEventStallType && isBarnAccountStablingOrBeddingRequest(sr) && matchesBarn;
                    }
                    return stablingRequestMatchesEventStallType && sr.submittedEntry === updatedFormattedEntryData.eventEntry.id;
                });

                // Delete all the stabling requests that need to be deleted
                for (let i = 0; i < stablingRequestsToDelete.length; i++) {
                    const stablingRequestToDelete = stablingRequestsToDelete[i];
                    await deleteStablingRequest({ id: stablingRequestToDelete.id });
                }
            } else if (updatedItem.item.previousBeddingRequest) {
                // Find all bedding requests that need to be deleted
                const beddingRequestsToDelete: BeddingRequest[] = (previousBeddingRequests ?? []).filter((br: BeddingRequest) => {
                    const beddingRequestMatchesEventStallType = br.beddingType === updatedItem.item.eventBeddingTypeId;
                    if (forBarnAccount) {
                        const matchesBarn = !updatedFormattedEntryData.barnId || br.barnId === updatedFormattedEntryData.barnId;
                        return beddingRequestMatchesEventStallType && isBarnAccountStablingOrBeddingRequest(br) && matchesBarn;
                    }
                    return beddingRequestMatchesEventStallType && br.submittedEntry === updatedFormattedEntryData.eventEntry.id;
                });
                // Delete all the bedding requests that need to be deleted
                for (let i = 0; i < beddingRequestsToDelete.length; i++) {
                    const beddingRequestToDelete = beddingRequestsToDelete[i];
                    await deleteBeddingRequest({ id: beddingRequestToDelete.id });
                }
            } else if (updatedItem.item.previousEventEntryFee) {
                const eventEntryFeeToDelete = updatedItem.item.previousEventEntryFee;
                await deleteEventEntryFee({ id: eventEntryFeeToDelete.id });
            }

            // Remove the item at the specified row index
            updatedItemsList = updatedItemsList.filter((r: FormattedRow, i: number) => i !== rowIndex && r.item.value !== updatedItem.item.value);
            updatedFormattedEntryData = {
                ...updatedFormattedEntryData,
                rows: updatedItemsList,
            };

            const formattedEntryDataList: FormattedEntryData[] = [
                ...formattedEntryData.slice(0, entryIndex),
                updatedFormattedEntryData,
                ...formattedEntryData.slice(entryIndex + 1),
            ];
            setFormattedEntryData(formattedEntryDataList);
            onItemsChange?.(formattedEntryDataList);
            setIsRemoving(false);
        };

        const handleQuantityChange = (entryIndex: number, rowIndex: number, newQuantity: number) => {
            if (newQuantity === 0) {
                // Remove the item if quantity is 0
                handleRemoveItem(entryIndex, rowIndex);
            } else {
                // Update the quantity in the state
                let updatedFormattedEntryData: FormattedEntryData = formattedEntryData[entryIndex];
                let updatedItemsList: FormattedRow[] = [...updatedFormattedEntryData.rows];

                updatedItemsList[rowIndex] = {
                    ...updatedItemsList[rowIndex],
                    quantity: newQuantity,
                };

                updatedFormattedEntryData = {
                    ...updatedFormattedEntryData,
                    rows: updatedItemsList,
                };

                const formattedEntryDataList: FormattedEntryData[] = [
                    ...formattedEntryData.slice(0, entryIndex),
                    updatedFormattedEntryData,
                    ...formattedEntryData.slice(entryIndex + 1),
                ];
                setFormattedEntryData(formattedEntryDataList);
                onItemsChange?.(formattedEntryDataList);
                markDirty();
            }
        };

        const calculatePriceOfRow = (pricePerItem: number, tax: number, quantity: number) => {
            const basePrice = pricePerItem * quantity;
            const taxRate = tax / 100;
            const fullPrice = basePrice * taxRate + basePrice;
            return fullPrice;
        };

        const handleCreateDataFromRow = async (row: FormattedRow, formattedData: FormattedEntryData) => {
            const currentQuantity: number = row.quantity;
            const currentItem: StablingAndExtrasItem = row.item;
            const forBarnAccount = isBarnAccountEntry(formattedData);
            const currentEntry = formattedData.eventEntry;
            const barnEntryIds = (formattedData.linkedEntries ?? []).map((e) => e.id).filter(Boolean) as string[];
            const entryIdsForSave = forBarnAccount ? barnEntryIds : currentEntry ? [currentEntry.id] : [];
            const barnIdForSave = forBarnAccount ? formattedData.barnId || currentEntry.barnId : currentEntry.barnId;

            let newItem: any = {};

            // If row is a EventStallType
            const currentEventStallType: EventStallType | undefined = currentItem.eventStallType;
            if (currentEventStallType) {
                const pricePerItem = parseFloat(currentEventStallType?.pricePerStall || "0");
                const tax = parseFloat(currentEventStallType.taxPerStall || "0");
                const totalPrice = calculatePriceOfRow(pricePerItem, tax, currentQuantity);
                if (totalPrice > 0) {
                    const input: CreateStablingRequestInput = {
                        eventId: event.id,
                        personId: user.id,
                        stallTypeId: currentItem.eventStallType?.id || null,
                        quantityNeeded: currentQuantity,
                        trainerId: currentEntry.trainer?.id || currentEntry.trainerId || null,
                        basePrice: pricePerItem,
                        taxA: tax,
                        totalPrice: totalPrice,
                        status: "saved", //TO DO: update depending on event accept entry policy
                        requestNote: stablingNote || "",
                        barnId: barnIdForSave || null,
                        splitAcrossEntries: forBarnAccount ? true : undefined,
                        entryIds: entryIdsForSave.length > 0 ? entryIdsForSave : null,
                        submittedEntry: forBarnAccount ? null : currentEntry?.id || "",
                    };
                    const createResult = await createStablingRequest(input);
                    if (createResult.isSuccess) {
                        newItem = {
                            type: "stall",
                            item: createResult.result,
                        };
                    }
                }
            }

            // If row is an EventBeddingType
            let currentEventBeddingType: EventBeddingType | undefined = currentItem.eventBeddingType;
            const currentEventBeddingTypeId: string | undefined = currentItem.eventBeddingTypeId;

            // Get the bedding type from the stabling options
            if (!currentEventBeddingType && currentEventBeddingTypeId) {
                const eventBeddingTypeQuery = await getEventBeddingTypeById(currentEventBeddingTypeId);
                if (eventBeddingTypeQuery.isSuccess) {
                    currentEventBeddingType = eventBeddingTypeQuery.result;
                }
            }

            if (currentEventBeddingType) {
                const pricePerItem = parseFloat(currentEventBeddingType?.pricePerBag || "0");
                const tax = parseFloat(currentEventBeddingType.taxPerBag || "0");
                const totalPrice = calculatePriceOfRow(pricePerItem, tax, currentQuantity);
                if (totalPrice > 0) {
                    const input: CreateBeddingRequestInput = {
                        eventId: event.id,
                        personId: user.id,
                        beddingType: currentItem.eventBeddingTypeId || null,
                        quantityNeeded: currentQuantity,
                        trainerId: currentEntry.trainer?.id || currentEntry.trainerId || null,
                        basePrice: pricePerItem,
                        totalPrice: totalPrice,
                        status: "saved", //TO DO: update depending on event accept entry policy
                        barnId: barnIdForSave || null,
                        splitAcrossEntries: forBarnAccount ? true : undefined,
                        entryIds: entryIdsForSave.length > 0 ? entryIdsForSave : null,
                        submittedEntry: forBarnAccount ? null : currentEntry?.id || "",
                    };
                    const createResult = await createBeddingRequest(input);
                    if (createResult.isSuccess) {
                        newItem = {
                            type: "bedding",
                            item: createResult.result,
                        };
                    }
                }
            }

            // If row is an EventFee
            const currentEventFee: EventFee | undefined = currentItem.eventFee;
            if (currentEventFee) {
                const input: CreateEventEntryFeeInput = {
                    eventId: event.id,
                    name: currentItem.label,
                    status: "saved",
                    amount: currentEventFee.amount,
                    quantity: currentQuantity,
                    entryId: forBarnAccount ? null : currentEntry?.id || null,
                    eventFeeId: currentEventFee.id,
                    feeId: currentEventFee.feeId || "",
                    description: forBarnAccount && barnIdForSave
                        ? barnAccountFeeDescription(barnIdForSave, currentItem.description)
                        : currentItem.description,
                    createdBy: user.id,
                };
                const createResult = await createEventEntryFee(input);
                if (createResult.isSuccess) {
                    newItem = {
                        type: "extras",
                        item: createResult.result,
                    };
                } else {
                    console.error("Could not save the fee: " + currentItem.label);
                    console.error(createResult);
                }
            }

            return newItem;
        };

        const handleUpdateDataFromRow = async (row: FormattedRow) => {
            let newItem: any = {};

            const currentQuantity: number = row.quantity;
            const currentItem: StablingAndExtrasItem = row.item;

            // If row is an EventStallType
            const currentEventStallType: EventStallType | undefined = currentItem.eventStallType;
            if (currentEventStallType) {
                const input: UpdateStablingRequestInput = {
                    id: currentItem.previousStablingRequest?.id || "",
                    quantityNeeded: currentQuantity,
                    status: "saved", //TO DO: update depending on event accept entry policy
                    requestNote: stablingNote || "",
                };
                const updateResult = await updateStablingRequest(input);
                if (updateResult.isSuccess) {
                    newItem = {
                        type: "stabling",
                        item: updateResult.result,
                    };
                }
            }

            // If row is an EventBeddingType
            const currentEventBeddingTypeId: string | undefined = currentItem.eventBeddingTypeId;
            if (currentEventBeddingTypeId) {
                const input: UpdateBeddingRequestInput = {
                    id: currentItem.previousBeddingRequest?.id || "",
                    quantityNeeded: currentQuantity,
                    status: "saved", //TO DO: update depending on event accept entry policy
                };
                const updateResult = await updateBeddingRequest(input);
                if (updateResult.isSuccess) {
                    newItem = {
                        type: "bedding",
                        item: updateResult.result,
                    };
                }
            }

            // If row is an EventFee
            const currentEventFee: EventFee | undefined = currentItem.eventFee;
            if (currentEventFee) {
                const input: UpdateEventEntryFeeInput = {
                    id: currentItem.previousEventEntryFee?.id || "",
                    quantity: currentQuantity,
                    status: "saved", //TO DO: update depending on event accept entry policy
                };
                const updateResult = await updateEventEntryFee(input);
                if (updateResult.isSuccess) {
                    newItem = {
                        type: "extras",
                        item: updateResult.result,
                    };
                }
            }
            return newItem;
        };

        const handleSaveData = async () => {
            setIsSaving(true);

            let updatedFormattedData: FormattedEntryData[] = [];

            for (let i = 0; i < formattedEntryData.length; i++) {
                const formattedData: FormattedEntryData = formattedEntryData[i];
                const currentRows = formattedData.rows;

                let updatedRows: FormattedRow[] = [];

                for (let j = 0; j < currentRows.length; j++) {
                    const currentRow = currentRows[j];
                    // TO DO: handle any errors that occur when trying to create new stabling requests
                    if (currentRow.item.previousStablingRequest || currentRow.item.previousBeddingRequest || currentRow.item.previousEventEntryFee) {
                        let newItem: any = null;

                        // Check if stabling request quantity has changed
                        if (currentRow.item.previousStablingRequest && currentRow.item.previousStablingRequest?.quantityNeeded !== currentRow.quantity) {
                            newItem = await handleUpdateDataFromRow(currentRow);
                        }

                        // Check if stabling note has changed
                        if (currentRow.item.previousStablingRequest && currentRow.item.previousStablingRequest?.requestNote !== stablingNote) {
                            newItem = await handleUpdateDataFromRow(currentRow);
                        }

                        // Check if bedding request quantity has changed
                        if (currentRow.item.previousBeddingRequest && currentRow.item.previousBeddingRequest?.quantityNeeded !== currentRow.quantity) {
                            newItem = await handleUpdateDataFromRow(currentRow);
                        }

                        // Check if extra fee quantity has changed
                        if (currentRow.item.previousEventEntryFee && currentRow.item.previousEventEntryFee?.quantity !== currentRow.quantity) {
                            newItem = await handleUpdateDataFromRow(currentRow);
                        }

                        if (newItem) {
                            const updatedRow: FormattedRow = {
                                item: {
                                    ...currentRow.item,
                                    previousStablingRequest: newItem.type === "stall" ? (newItem.item as StablingRequest) : undefined,
                                    previousBeddingRequest: newItem.type === "bedding" ? (newItem.item as BeddingRequest) : undefined,
                                    previousEventEntryFee: newItem.type === "extras" ? (newItem.item as EventEntryFee) : undefined,
                                },
                                quantity: currentRow.quantity,
                            };
                            updatedRows.push(updatedRow);
                        } else {
                            updatedRows.push(currentRow);
                        }
                    } else {
                        const newItem: any = await handleCreateDataFromRow(currentRow, formattedData);
                        if (newItem) {
                            const updatedRow: FormattedRow = {
                                item: {
                                    ...currentRow.item,
                                    previousStablingRequest: newItem.type === "stall" ? (newItem.item as StablingRequest) : undefined,
                                    previousBeddingRequest: newItem.type === "bedding" ? (newItem.item as BeddingRequest) : undefined,
                                    previousEventEntryFee: newItem.type === "extras" ? (newItem.item as EventEntryFee) : undefined,
                                },
                                quantity: currentRow.quantity,
                            };
                            updatedRows.push(updatedRow);
                        }
                    }
                }
                const updatedFormattedEntryData: FormattedEntryData = {
                    id: formattedData.id,
                    title: formattedData.title,
                    eventEntry: formattedData.eventEntry,
                    rows: updatedRows,
                    isBarnAccount: formattedData.isBarnAccount,
                    barnId: formattedData.barnId,
                    barnName: formattedData.barnName,
                    linkedEntries: formattedData.linkedEntries,
                };

                updatedFormattedData.push(updatedFormattedEntryData);
            }

            setFormattedEntryData(updatedFormattedData);
            onItemsChange?.(updatedFormattedData);
            clearDirty();
            setIsSaving(false);
        };

        const barnAccountSections = useMemo(() => {
            if (eventEntries.length === 0) return [];
            const seenBarnIds = new Set<string>();
            return formattedEntryData.filter((d) => {
                if (!isBarnAccountEntry(d)) return false;
                if (d.id === "barn-account") return false;
                const key = d.barnId || d.id;
                if (seenBarnIds.has(key)) return false;
                seenBarnIds.add(key);
                return true;
            });
        }, [formattedEntryData, eventEntries.length]);

        const perEntryStablingData = useMemo(() => formattedEntryData.filter((d) => !isBarnAccountEntry(d)), [formattedEntryData]);

        const entryStablingByEntryId = useMemo(() => {
            const map = new Map<string, FormattedEntryData>();
            for (const data of perEntryStablingData) {
                if (data.eventEntry?.id) map.set(data.eventEntry.id, data);
            }
            return map;
        }, [perEntryStablingData]);

        const barnDisplayGroups = useMemo(() => groupCompletedEntriesByBarn(eventEntries), [eventEntries]);

        const entriesWithoutBarn = useMemo(() => {
            const idsUnderBarn = new Set<string>();
            for (const group of barnDisplayGroups) {
                for (const entry of group.entries) {
                    if (entry.id) idsUnderBarn.add(entry.id);
                }
            }
            return perEntryStablingData.filter((d) => !d.eventEntry?.barnId || !idsUnderBarn.has(d.eventEntry.id));
        }, [barnDisplayGroups, perEntryStablingData]);

        const entryIndexById = useMemo(() => buildEntryIndexById(eventEntries), [eventEntries]);

        interface RenderStablingCardOptions {
            cardTitle: string;
            titleSecondaryLine?: string;
            titleTertiaryLine?: string;
            leadingEmoji?: string;
            emptyMessage?: string;
            defaultExpanded?: boolean;
        }

        const renderStablingItemsCard = (data: FormattedEntryData, options: RenderStablingCardOptions) => {
            const {
                cardTitle,
                titleSecondaryLine,
                titleTertiaryLine,
                leadingEmoji,
                emptyMessage = "Choose items to order above.",
                defaultExpanded = false,
            } = options;
            const stateIndex = formattedEntryData.findIndex((d) => d.id === data.id);
            if (stateIndex < 0) return null;

            const stallCount = data.rows.filter((r) => r.item.label.toLowerCase().includes("stall")).reduce((sum, r) => sum + r.quantity, 0);

            return (
                <div key={data.id} className="mb-2">
                    <SubSectionCard
                        title={cardTitle}
                        titleSecondaryLine={titleSecondaryLine}
                        titleTertiaryLine={titleTertiaryLine}
                        leadingEmoji={leadingEmoji}
                        allowWrappedTitle={true}
                        defaultExpanded={defaultExpanded}
                        stallCount={stallCount}>
                        <StablingExtrasOrderEditor
                            rows={data.rows}
                            dropdownMenuOptions={dropdownMenuOptions}
                            onSelectItem={(item) => handleOnChange(stateIndex, item)}
                            onQuantityChange={(rowIndex, quantity) => handleQuantityChange(stateIndex, rowIndex, quantity)}
                            onRemoveItem={(rowIndex) => handleRemoveItem(stateIndex, rowIndex)}
                            emptyMessage={emptyMessage}
                            selectInputId={`select-stabling-${data.id}`}
                        />
                    </SubSectionCard>
                </div>
            );
        };

        const renderEntryStablingCard = (entry: EventEntry, data: FormattedEntryData) => {
            const stateIndex = formattedEntryData.findIndex((d) => d.id === data.id);
            if (stateIndex < 0) return null;

            const entryIndex = entry.id ? entryIndexById.get(entry.id) : undefined;
            const entryNumber = entryIndex !== undefined ? entryIndex + 1 : 1;
            const stallCount = data.rows.filter((r) => r.item.label.toLowerCase().includes("stall")).reduce((sum, r) => sum + r.quantity, 0);

            const onlyId = expandStablingOnlyForEventEntryId;
            const deepLinkMatchesSomeEntry =
                onlyId != null &&
                onlyId !== "" &&
                eventEntries.some((e) => e.id === onlyId);
            const defaultExpanded =
                onlyId != null && onlyId !== "" && deepLinkMatchesSomeEntry
                    ? entry.id === onlyId || data.id === onlyId || data.eventEntry?.id === onlyId
                    : false;

            return (
                <EntrySubSectionCard
                    key={data.id}
                    remountKey={`${data.id}-${stablingAccordionFocusKey}`}
                    entry={entry}
                    entryNumber={entryNumber}
                    defaultExpanded={defaultExpanded}
                    collapsedHeaderTrailing={<EntryStatusChip isComplete={entry.status === "complete"} />}
                    stallCount={stallCount}>
                    <StablingExtrasOrderEditor
                        rows={data.rows}
                        dropdownMenuOptions={dropdownMenuOptions}
                        onSelectItem={(item) => handleOnChange(stateIndex, item)}
                        onQuantityChange={(rowIndex, quantity) => handleQuantityChange(stateIndex, rowIndex, quantity)}
                        onRemoveItem={(rowIndex) => handleRemoveItem(stateIndex, rowIndex)}
                        emptyMessage="Choose items to order for this entry above."
                        selectInputId={`select-stabling-${data.id}`}
                    />
                </EntrySubSectionCard>
            );
        };

        return (
            <>
                {isSectionLoading ? (
                    <SubSectionCardsSkeleton count={Math.max(eventEntries.length + barnAccountSections.length, 1)} />
                ) : isSaving || isRemoving ? (
                    <SubSectionCardsSkeleton count={Math.max(formattedEntryData.length, 1)} />
                ) : (
                    <>
                        {eventEntries.length > 0 ? (
                            <>
                                {barnDisplayGroups.map((barnGroup, barnGroupIndex) => {
                                    const barnData =
                                        barnAccountSections.find((b) => b.barnId === barnGroup.barnId) ||
                                        barnAccountSections.find((b) => (b.linkedEntries ?? []).some((e) => e.barnId === barnGroup.barnId));
                                    const hasNextBarnGroup = barnGroupIndex < barnDisplayGroups.length - 1;

                                    return (
                                        <div key={barnGroup.barnId}>
                                            {eventEntries.length > 0 && barnData
                                                ? renderStablingItemsCard(barnData, {
                                                      cardTitle: barnGroup.barnName,
                                                      titleTertiaryLine: "Barn Split Account — supplies to be split in the show office later",
                                                      leadingEmoji: "🏠",
                                                      emptyMessage:
                                                          "Choose items above for shared barn purchases (e.g. shavings, bedding, feed & extras).",
                                                      defaultExpanded: false,
                                                  })
                                                : null}
                                            {barnGroup.entries.map((entry) => {
                                                const data = entryStablingByEntryId.get(entry.id);
                                                if (!data) return null;
                                                return renderEntryStablingCard(entry, data);
                                            })}
                                            {hasNextBarnGroup ? (
                                                <div
                                                    role="separator"
                                                    aria-hidden="true"
                                                    className="entry-form-divider--spaced"
                                                />
                                            ) : null}
                                        </div>
                                    );
                                })}
                                {entriesWithoutBarn.length > 0 && barnDisplayGroups.length > 0 ? (
                                    <div role="separator" aria-hidden="true" className="entry-form-divider" />
                                ) : null}
                                {entriesWithoutBarn.map((data) => renderEntryStablingCard(data.eventEntry, data))}
                            </>
                        ) : (
                            <p>No entries found. Please go back up and add an entry.</p>
                        )}
                        <div role="separator" aria-hidden="true" className="entry-form-divider" />
                        <IonRow>
                            <IonCol className="p-0 pb-2">
                                <IonCard className="mx-0 my-0 subsection-panel-card">
                                    <IonCardContent className="px-3 py-2 bg-white">
                                        <IonLabel className="font-weight-bold d-block mb-1 text-primary subsection-panel-card__title">
                                            Stabling Notes
                                        </IonLabel>
                                        <BasicTextarea
                                            value={stablingNote}
                                            placeHolder="Enter any additional notes you would like included with your above stall requests."
                                            rows={4}
                                            isReadOnly={false}
                                            saveChange={(value) => {
                                                setStablingNote(value);
                                                markDirty();
                                            }}
                                        />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>
                    </>
                )}
            </>
        );
    }
);

StablingAndExtrasSection.displayName = "StablingAndExtrasSection";

export default StablingAndExtrasSection;
